import React, { ReactNode, useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TProjetoHistorico } from 'src/components/projetos/types';
import theme from 'src/theme/light';
import { ChangeState, ORIGEM_HISTORICO_V1, ORIGEM_HISTORICO_V2 } from 'src/components/projetos/historico/Historico';
import { useObservable } from 'src/framework/store/storeUtil';
import { categorias_componentes$, cidades$, componentes$, estruturaDistribuicao$ } from 'src/store/global.store';
import { LISTA_TIPO_PROJETO, STATUS_PROJETO, STATUS_PROJETO_PAGAMENTO, TIPOS_SOLUCAO_OPTIONS, TIPO_FATURAMENTO } from 'src/components/projetos/constants';
import { TermosFinalizar } from 'src/components/projetos/containers/TermosFinalizar';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Alert } from '@mui/material';
import { PermissionUtil } from 'src/framework/commons/PermissionUtil';
import moment from 'moment';
import { ID_INVERSOR, ID_INVERSOR_HIBRIDO, ID_MICROINVERSOR, LISTA_INVERSOR_OFFGRID, inversoresMap } from 'src/components/componentes/types';
import { arraySort, getTipoProjeto } from '../../HistoricoMigrado/utils/actions';
import { green, orange, red } from '@mui/material/colors';
import { CategoriasComponentesHistorico, DEFAULT_CATEGORIAS_COMPONENTES } from '../constants';
import { Pedido } from 'src/components/vendas/types';
import { Projeto } from 'src/components/projetos/EdicaoAvancada/type';
import { TIPOS_BATERIAS_OPTIONS } from 'src/components/projetos/offgrid/FormClienteOffGrid/containers/TipoBateriaInput';
import './styles.css';
import { Formater, TypeRecords } from 'src/framework/commons/Formater';
import { isHibrido } from 'src/components/projetos/utils/projetoHibrido';

type CustomProjeto = Projeto & {
  visita_tecnica_texto?: {
    checklist: string[] | ({ texto: string, itens?: { texto?: string }[] })[] 
  }
  tipo?: string
  segmento?: string
  dados_termo?: {
    aceito_em: string
    texto: string[]
    user_id: number;
  }
}

interface ComparisonTableProps {
  dados: TProjetoHistorico[];
  changeState: ChangeState;
  pedido?: Pedido;
  projeto?: CustomProjeto;
}

type OptionTensao = {
  id: string;
  nome: string;
}

type OptionTipoBateria = {
  id: string;
  nome: string;
}

const OPTIONS_TENSAO: OptionTensao[] = [
  {
    id: '127',
    nome: '127V Monofásico (CA)',
  },
  {
    id: '220',
    nome: '220V Monofásico (CA)',
  },
  {
    id: '127/220',
    nome: '220V Trifásico (CA)',
  },
  {
    id: '220/380',
    nome: '380V Trifásico (CA)',
  },
  {
    id: '24',
    nome: '24V (CC)',
  },
  {
    id: '48',
    nome: '48V (CC)',
  }
];

const OPTIONS_TENSAO_ON: OptionTensao[] = [
  {
    id: '1',
    nome: '220V'
  },
  {
    id: '2',
    nome: '220/380V'
  },

];

const styles = makeStyles({
  titulo: {
      fontWeight: "bold",
      border: '1px solid grey'
  },
  alterado: {
      backgroundColor: "#BBE6C9",
      border: '1px solid grey'
  },
  removido: {
      backgroundColor: theme.palette.error.light,
  },
  adicionado: {
      backgroundColor: theme.palette.primary.light,
  },
  header: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: "bold",
  },
  historico_selecionado: {
      backgroundColor: theme.palette.grey[200],
      border: '1px solid grey'
  },
  icon_alterou : {
      color: theme.palette.warning.main,
      fontSize: "14px"
  },
});

const TabelaComparacaoProjetos: React.FC<ComparisonTableProps> = (props) => {
  const classes = styles();
  const componentes = useObservable(componentes$);
  const categoriasComponentes = useObservable(categorias_componentes$);
  const estruturas = useObservable(estruturaDistribuicao$);
  const cidades = useObservable(cidades$);
  const permissoes = PermissionUtil.getPermissions();
  const [categoriaComponentesState, setCategoriaComponentesState] = useState<CategoriasComponentesHistorico[]>(DEFAULT_CATEGORIAS_COMPONENTES);
  const avulso = props?.projeto?.tipo === 'avulso';

  function comparaCategoria(a: any, b: any): number {
    const ordemCategorias = ['modulos', 'inversores', 'itens_estrutura', 'acessorios'];
    const idxA = ordemCategorias.indexOf(a.grupo);
    const idxB = ordemCategorias.indexOf(b.grupo);
    if (idxA !== -1 && idxB !== -1) {
      return idxA - idxB;
    }
    else if (idxA !== -1) {
      return -1;
    } else if (idxB !== -1) {
      return 1;
    }
    return 0;
  }
  function getTipoPagamento(tipo: number) {
    switch (tipo) {
      case 1:
          return "Apenas Gerador sem Instalação";
      case 2:
          return "Gerador com Serviço de Instalação";
      default:
          return "";
    }
}

  function formatData(data: any): string {
    if (!data) return "";
    if (String(data)?.includes("GMT")) {
      return moment(data).utc().format("DD/MM/YYYY - HH:mm:ss");
    } else if (String(data)?.includes('T')) {
      const formated =  String(data)?.replace('T', ' ')?.replace('Z', '');
      return moment(formated).format("DD/MM/YYYY - HH:mm:ss");
    }
    return moment(data).format("DD/MM/YYYY - HH:mm:ss");
  }

  const handleValorTotal = (projeto: any, pedido: any, descontoPedido: boolean) => {
    if (projeto?.origem === ORIGEM_HISTORICO_V1 && pedido && descontoPedido) {
      const historicoProjeto = descontoPedido ? (props?.projeto || projeto) : projeto;
      return Number(((historicoProjeto?.valor || 0) - (historicoProjeto?.valor_desconto || 0) + (historicoProjeto?.valor_servico_pis_cofins || 0) - (pedido?.valor_desconto ? pedido.valor_desconto : 0))) || 0;
    } else {
      return Number(((projeto?.valor || 0) - (projeto?.valor_desconto || 0) + (projeto?.valor_servico_pis_cofins || 0))) || 0;
    }
  }

  const handleValorDesconto = (projeto: any, pedido: any, descontoPedido: boolean) => {
    if (projeto?.origem === ORIGEM_HISTORICO_V1 && pedido && descontoPedido) {
      const historicoProjeto = descontoPedido ? (props?.projeto || projeto) : projeto;
      return Number(((historicoProjeto?.valor || 0) - (historicoProjeto?.valor_desconto || 0) - (pedido?.valor_desconto ? pedido.valor_desconto : 0))) || 0;
    } else {
      return Number(((projeto?.valor || 0) - (projeto?.valor_desconto || 0) || 0)) || 0;
    }
  }

  const exibeCamposTermosProjeto = useMemo(()=>{
    const visitaTecnicaChecklist = props?.projeto?.visita_tecnica_texto?.checklist || []
    
    const tipoProjeto = props?.projeto?.tipo || null;
    if (!tipoProjeto) return false;

    // Exibe os termos do projeto para projetos KIT
    if (tipoProjeto === 'kit') return true;

    const possuiTermos = !!props?.projeto?.dados_termo;
    if (!possuiTermos) return false;

    if (!visitaTecnicaChecklist) return true;

    // Não exibe os termos de projeto para projetos dimensionados ou personalizados que tenham termos de visita técnica na forma nova.
    if (typeof visitaTecnicaChecklist[0] === 'object') return false;
    
    // Exibe os termos para o restante dos casos
    return true
  },[props])

  const dimensionadoOffgrid = useMemo(()=> {
    const segmentoProjeto = props?.projeto?.segmento || null;
    const tipoProjeto = props?.projeto?.tipo || null;
    if (tipoProjeto === 'dimensionado' && segmentoProjeto?.includes('off')) return true;
    return false;
  },[props]); 
  
  const dimensionadoHibrido = useMemo(()=> {
    const codigoVenda = props?.projeto?.codigo_venda || "";
    const tipoProjeto = props?.projeto?.tipo || "";
    return isHibrido(codigoVenda, tipoProjeto) && tipoProjeto === 'dimensionado';
  },[props]);

  const personalizadoHibrido = useMemo(()=> {
    const codigoVenda = props?.projeto?.codigo_venda || "";
    const tipoProjeto = props?.projeto?.tipo || "";
    return isHibrido(codigoVenda, tipoProjeto) && tipoProjeto === 'personalizado';
  },[props]);

  const personalizadoOffgrid = useMemo(()=> {
    const segmentoProjeto = props?.projeto?.segmento || null;
    const tipoProjeto = props?.projeto?.tipo || null;
    if (tipoProjeto === 'personalizado' && segmentoProjeto?.includes('off')) return true;
    return false;
  }, [props]);

   const renderTermosVisitaTecnica = useCallback((projeto: any) => {
        if (!projeto || !projeto.termos_aceite) return null;

        const termosVisitaTecnica = projeto.termos_aceite
        const formatoListaDeItens = termosVisitaTecnica && typeof termosVisitaTecnica[0] === 'string'

        if (formatoListaDeItens) {
            return projeto.termos_aceite?.map((termo: any, index: number) => {
                return (
                    <div className="X12" key={projeto.id + "-termos-" + index} style={{marginBottom: "10px"}}>
                        <Alert severity="success">
                            {termo}
                        </Alert>
                    </div>
                )
            })
        }

        return projeto.termos_aceite?.map((termo: any, index: number) => {
            if (typeof termo === 'string') return null;

            return (
                <div className="X12" key={projeto.id + "-termos-" + index} style={{marginBottom: "10px"}}>
                    <Alert severity="success">
                      {termo.texto}
                      
                      {termo.itens && !!termo.itens.length && (
                          <ul style={{ marginLeft: '18px' }}>
                              {termo.itens.map((item: any) => (
                                  <li key={item.texto}>
                                      {item.texto}
                                  </li>
                              ))}
                          </ul>
                      )}
                    </Alert>
                </div>
            )
        })
    }, [])

  const maskDate = (date: string | Date, datetime: boolean)=>{
    if(date){
        const data = new Date(date);

        if (datetime){
            const formattedData = data.toLocaleString('pt-BR', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            });
            return formattedData;
        }else{
            const formattedData = data.toLocaleString('pt-BR', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
            });
            return formattedData;
        }    
    }else return "-";
}

  const parseTensaoOnGrid = (tensao: string) => {
    return OPTIONS_TENSAO_ON.find((item: OptionTensao)=> String(item.id) === String(tensao))?.nome;
  }
  const parseTensaoOffgrid = (tensao: string) => {
    return OPTIONS_TENSAO.find((item: OptionTensao)=> item.id === tensao)?.nome;
  }
  const parseTipoBateria = (tipoBateria: string) => {
    return TIPOS_BATERIAS_OPTIONS.find((item: OptionTipoBateria)=> item.id === tipoBateria)?.nome;
  }
  const compareRow = ((campo: string, projetos: any, changed: boolean): ReactNode =>{
    let allRemoved: any[] = [];

    switch(campo){
      case "solucao_sugerida":
        return(<>
          {projetos.map((_historico: any)=>{
            let bodySolucao: ReactNode | null = null;
            let tableSolucao: ReactNode | null = null;
            const { projeto } = _historico;
            if (projeto?.solucao_sugerida && projeto?.tipo === "dimensionado"){
              const _solucao = projeto?.solucao_sugerida?.componentes?.filter((i:any) =>i.quantidade > 0);
              const componentList = _solucao?.map((comp:any) => {
                const _componente = componentes?.data?.find((c: any) => c?.id === comp?.id);
                return {
                  ...comp,
                  _componente,
                  grupo: categoriaComponentesState?.find((cat:CategoriasComponentesHistorico)=>cat.id ===_componente?.categoria_componentes_id)?.grupo
                }
              })?.sort((a: any, b: any)=> comparaCategoria(a, b));
              bodySolucao = componentList?.map((comp: any)=>{
                const _componente = comp?._componente;
                return(<TableRow key={comp?.id}>
                  {PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_CODIGO_COMPONENTE,permissoes)&&(<TableCell>{comp?.id}</TableCell>)}
                  <TableCell width="auto">{comp?.quantidade}</TableCell>
                  <TableCell width="auto">{_componente?.nome || _componente?.modelo}</TableCell>
                </TableRow>)
              })
            }
            if(bodySolucao){
              tableSolucao = 
              <TableContainer>
                  <Table size="small">
                      <TableHead>
                          <TableRow>
                              {PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_CODIGO_COMPONENTE,permissoes)&&(<TableCell style={{fontWeight: 'bold'}} align="left">Código</TableCell>)}
                              <TableCell style={{fontWeight: 'bold'}} width="auto">Qtde.</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} width="auto">Descrição</TableCell>
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {bodySolucao}
                      </TableBody>
                  </Table>
              </TableContainer>
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{tableSolucao || "-"}</TableCell>)
          })}
        </>)
      case "componentes":
        return(<>
          {projetos.map((_historico: any)=>{
            let bodyComponentes: ReactNode | null = null;
            let tableComponentes: ReactNode | null = null;
            const { projeto } = _historico;
            if (projeto?.componentes){
              const _componentes = projeto?.componentes?.filter((i:any) =>i.quantidade > 0/* && i.deleted_at === null*/);
              const componentList = _componentes?.map((comp:any) => {
                const _componente = componentes?.data?.find((c: any) => c?.id === comp?.componente_id);
                return {
                  ...comp,
                  _componente,
                  grupo: categoriaComponentesState?.find((cat:CategoriasComponentesHistorico)=>cat.id ===_componente?.categoria_componentes_id)?.grupo
                }
              })?.sort((a: any, b: any)=> comparaCategoria(a, b));
              
              bodyComponentes = componentList?.map((comp: any)=>{
                let removed: any[] = [];
                let added: any[] = [];
                let edited: any[] = [];

                const _componente = comp?._componente;
                let valorOriginal = comp?.valor_unitario_original;
                if (projeto?.origem === ORIGEM_HISTORICO_V1) {
                  const componentesV1 = projeto?.dados_migracao?.componentesV1 || [];
                  const componenteV1 = componentesV1?.find((c: any) => Number(c.CodigoProduto) === _componente?.id);
                  if (componenteV1?.ValorOriginal) valorOriginal = componenteV1?.ValorOriginal;
                }
                
                let color = '';
                const firstProject = projetos[0]?.projeto;
                const findFirst = firstProject?.componentes?.filter((i:any) =>i.quantidade > 0)?.find((i:any) => i?.componente_id === _componente?.id);
                const current = projetos?.find((i:any) => Number(i?.id) === Number(_historico?.id));
                const findCurrent = current?.projeto?.componentes?.find((ii:any) => ii?.componente_id === comp?.componente_id);
                const wasRemoved = allRemoved?.find((id:any) => id === comp?.componente_id);
                
                if (projetos?.length > 1) {
                  if (!findFirst || (wasRemoved && findCurrent)) { 
                    color = green[200];
                    added.push(_componente?.id);
                  } else if (findFirst?.quantidade !== comp?.quantidade) {
                    color = orange[200];
                    edited.push(_componente?.id);
                  }
                  
                  const filter = projetos?.filter((i:any) => Number(i?.id) !== Number(_historico?.id));
                  filter?.forEach((i:any) => {
                    const found = i?.projeto?.componentes?.find((ii:any) => ii?.componente_id === comp?.componente_id);
                    if (!found && !added?.includes(comp?.componente_id) && !edited?.includes(comp?.componente_id)) {
                      removed.push(comp?.componente_id);
                      if (!allRemoved?.includes(comp?.componente_id)) allRemoved.push(comp?.componente_id);
                      return;
                    }
                  })

                  if (removed?.includes(comp?.componente_id)) color = red[200];
                }
                
                return(<TableRow key={comp?.componente_id} style={{ backgroundColor: color }}>
                  {PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_CODIGO_COMPONENTE,permissoes)&&(<TableCell>{comp?.componente_id}</TableCell>)}
                  <TableCell align="left">{comp?.quantidade}</TableCell>
                  <TableCell align="left">{_componente?.nome || _componente?.modelo}</TableCell>
                  {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                      &&                             
                      (<TableCell align="left">{Formater.toString(Number(valorOriginal || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)}
                  {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                      &&                             
                      (<TableCell align="left">{Formater.toString(Number(comp?.valor_unitario || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)}
                  {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                      &&                             
                      (<TableCell align="left">{Formater.toString(Number((comp?.valor_unitario * comp?.quantidade) || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)}
                </TableRow>)
              })
            }
            if(bodyComponentes){
              tableComponentes = 
              <TableContainer>
                  <Table size="small">
                      <TableHead>
                          <TableRow>
                              {PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_CODIGO_COMPONENTE,permissoes)&&(<TableCell style={{fontWeight: 'bold'}} align="left">Código</TableCell>)}
                              <TableCell style={{fontWeight: 'bold'}} align="left">Qtde.</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Descrição</TableCell>
                              {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                                  &&
                                  (<TableCell style={{fontWeight: 'bold'}} align="left">Valor Original</TableCell>)}
                              {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                                  &&
                                  (<TableCell style={{fontWeight: 'bold'}} align="left">Valor Final</TableCell>)}
                              {(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_DETALHE_VISUALIZAR_VALORES,permissoes)) 
                                  &&
                                  (<TableCell style={{fontWeight: 'bold'}} align="left">Total Final</TableCell> )}
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {bodyComponentes}
                      </TableBody>
                  </Table>
              </TableContainer>
            }
            return(<TableCell className={classes.historico_selecionado}>{tableComponentes || "-"}</TableCell>)
          })}
        </>)
      case "tipo_faturamento":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const _tipoFaturamento = TIPO_FATURAMENTO.find((tipo: any) => tipo.value === projeto?.tipo_faturamento);
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {_tipoFaturamento?.text || "-"}              
            </TableCell>)
          })}
        </>)
      case "tipo":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            let _tipoProjeto = LISTA_TIPO_PROJETO.find((tipo: any) => tipo.value === projeto?.tipo);
            if (projeto?.origem === ORIGEM_HISTORICO_V1) {
              const projetoV1 = { ...projeto?.dados_migracao?.projetoV1, IsV2: projeto?.origem === ORIGEM_HISTORICO_V2 };
              const tipoProjeto = getTipoProjeto(projeto?.codigo_venda, projetoV1);
              _tipoProjeto = LISTA_TIPO_PROJETO.find((tipo: any) => tipo.value === tipoProjeto);
            }

            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {_tipoProjeto?.text || "-"}             
            </TableCell>)
          })}
        </>)
      case "status_projeto":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const status_projeto = STATUS_PROJETO.find((status: any)=>status.value === projeto?.status_projeto);
            return(<TableCell className={changed ? classes.alterado : changed ? classes.alterado : classes.historico_selecionado}>
              {status_projeto?.text || "-"}             
            </TableCell>)
          })}
        </>)      
      case "status_projeto_pagamento":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const status_projeto_pagamento = STATUS_PROJETO_PAGAMENTO.find((status: any)=>status.value === projeto?.status_projeto_pagamento);
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {status_projeto_pagamento?.text || "-"}             
            </TableCell>)
          })}
        </>)
      case "expirado":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {projeto?.expirado ? "Sim" : "Não"}             
            </TableCell>)
          })}
        </>)
      case "termos_projeto":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const projetoV1 = projeto?.dados_migracao?.projetoV1;
            const termoProjeto = projeto?.dados_termo || projetoV1;
            const termoData = projeto?.origem === ORIGEM_HISTORICO_V1 ? formatData(projetoV1?.DataProjetoAceito) : maskDate(projeto?.dados_termo?.aceito_em, true);
            const termoTexto = projeto?.origem === ORIGEM_HISTORICO_V1 ? "" : projeto?.dados_termo?.texto;
            const termoUsuario = projeto?.origem === ORIGEM_HISTORICO_V1 ? projetoV1?.CriadoPor : projeto?.dados_termo?.usuario;

            return(
            <TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              <Box style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
                {Array.isArray(termoTexto) ?
                  termoTexto.map((t, index) => {
                    return <>
                      <Box style={{display: "flex"}}>
                        <Box style={{display: "flex"}}>
                            <>{!!t ? <CheckBoxIcon style={{ color: theme.palette.secondary.main}}/> : <CheckBoxOutlineBlankIcon />}</>
                        </Box>
                        <Box style={{marginLeft: "10px"}}>
                            <div dangerouslySetInnerHTML={{__html: t || ""}}/>
                            {index >= termoTexto.length - 1 ?
                              <strong><p>Termo aceito em: {termoData || ""} por: {termoUsuario || ""}</p></strong>
                            : ""}
                        </Box>
                      </Box>
                    </>
                  })
                : <>
                  <Box style={{display: "flex"}}>
                    <Box style={{display: "flex"}}>
                        <>{!!termoProjeto ? <CheckBoxIcon style={{ color: theme.palette.secondary.main}}/> : <CheckBoxOutlineBlankIcon />}</>
                    </Box>
                    <Box style={{marginLeft: "10px"}}>
                        <div dangerouslySetInnerHTML={{__html: termoTexto || ""}}/>
                        <strong><p>Termo aceito em: {termoData || ""} por: {termoUsuario || ""}</p></strong>
                    </Box>
                  </Box>
                </>}
                
              </Box>
            </TableCell>)
          })}
        </>)
      case "previsao_faturamento":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const projetoV1 = projeto?.dados_migracao?.projetoV1;

            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {projeto?.dados_finalizar ? (
                <blockquote style={{borderRadius: '10px', margin: '24px 0', padding: '4px 24px', borderLeft: '5px solid rgb(0, 175, 57)', backgroundColor: 'rgb(226 226 226)'}}>
                  { projeto?.origem === ORIGEM_HISTORICO_V1 
                    ? (<div dangerouslySetInnerHTML={{__html: projetoV1?.TextoAceito || ""}}/>) 
                    : (<TermosFinalizar termos={{...projeto?.dados_finalizar, migrado: false, label:<></>}}/>) 
                  }
                </blockquote>
              ) : "-"}           
            </TableCell>)
          })}
        </>)
      case "aceitou_termos":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const projetoAceito = projeto?.origem === ORIGEM_HISTORICO_V1 ? projeto?.dados_migracao?.projetoV1?.AceitouOsTermos : projeto?.dados_termo?.aceito_em;
            return (<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>
              {projetoAceito ? "Sim" : "Não"}
            </TableCell>)
          })}
        </>)
      case "tensao":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const tensao = projeto?.solucao_sugerida?.inversor_tensao === 1 ? "127/220" : projeto?.solucao_sugerida?.inversor_tensao === 2 ? "220/380": null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{tensao || "-"}</TableCell>)
          })}
        </>)
      case "inversor_tipo_conexao":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const fase = projeto?.solucao_sugerida?.inversor_tipo_conexao === 1 ? "Monofásico" : (projeto?.solucao_sugerida?.inversor_tipo_conexao === 2 ? "Bifásico" : projeto?.solucao_sugerida?.inversor_tipo_conexao === 3 ? "Trifásico": null);
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{fase || "-"}</TableCell>)
          })}
        </>)
      case "tipo_inversor":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            let tipo = null;
            if(projeto?.tipo === 'dimensionado' && (projeto?.solucao_sugerida?.componentes || projeto?.origem === ORIGEM_HISTORICO_V1)){
              tipo = projeto?.solucao_sugerida?.tipo_inversor === ID_INVERSOR ? 'Inversor' : projeto?.solucao_sugerida?.tipo_inversor === ID_MICROINVERSOR ? 'Microinversor' : null;
            }else if (projeto?.tipo === 'personalizado'){
              const inversor = projeto?.componentes?.find((_c: any) => {
                const _componenteCache = componentes?.data?.find((c: any) => c?.id === _c?.componente_id);
                return _componenteCache?.categoria_componentes_id === ID_INVERSOR;
              });

              const micro = projeto?.componentes?.find((_c: any) => {
                const _componenteCache = componentes?.data?.find((c: any) => c?.id === _c?.componente_id);
                return _componenteCache?.categoria_componentes_id === ID_MICROINVERSOR;
              });

              const hibrido = projeto?.componentes?.find((_c: any) => {
                const _componenteCache = componentes?.data?.find((c: any) => c?.id === _c?.componente_id);
                return _componenteCache?.categoria_componentes_id === ID_INVERSOR_HIBRIDO;
              });

              const offGrid = projeto?.componentes?.find((_c: any) => {
                const _componenteCache = componentes?.data?.find((c: any) => c?.id === _c?.componente_id);
                return LISTA_INVERSOR_OFFGRID?.includes(_componenteCache?.categoria_componentes_id);
              });

              if (inversor || micro || offGrid || hibrido) {
                if (inversor) {
                  tipo = "Inversor";
                } else if (micro) {
                  tipo = "Microinversor";
                } else if (hibrido) {
                  tipo = "Inversor Híbrido";
                } else if (offGrid) {
                  tipo = "-";
                }
              }
            } else if (projeto?.tipo === 'kit' && !!projeto?.solucao_sugerida?.tipo_inversor) {
              tipo = inversoresMap[projeto?.solucao_sugerida?.tipo_inversor]?.text || null
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{tipo || "-"}</TableCell>)
          })}
        </>)
      case "fator_perdas":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const fator = Formater.toString(projeto?.solucao_sugerida?.fator_perdas || 0, TypeRecords.DECIMAL2) + "%"; 
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{fator}</TableCell>)
          })}
        </>)
      case "simultaneidade":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const simultaneidade = Formater.toString(projeto?.simultaneidade || 0, TypeRecords.DECIMAL2) + "%"; 
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{simultaneidade}</TableCell>)
          })}
        </>)
      case "potencia_desejada":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const potencia_desejada = projeto?.solucao_sugerida?.potencia ? Formater.toString(projeto?.solucao_sugerida?.potencia, TypeRecords.DECIMAL2)+" kWp" : null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{potencia_desejada || "-"}</TableCell>)
          })}
        </>)
      case "potencia":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const potencia = projeto?.potencia ? Formater.toString(projeto?.potencia, TypeRecords.DECIMAL2)+" kWp" : null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{potencia || "-"}</TableCell>)
          })}
        </>)
      case "concessionaria":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const {dados_concessionaria_payback} = projeto;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{dados_concessionaria_payback?.agente|| "-"}</TableCell>)
          })}
        </>)
      case "preco_kwh":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            const preco = Formater.toString(Number(projeto.preco_kwh || 0), TypeRecords.CURRENCY) || null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{preco || "-"}</TableCell>)
          })}
        </>)
      case "nome_modulo":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            let modelo = "";
            if(projeto?.componentes){
              /*const modulo = projeto?.componentes?.find((_c: any)=>{
                const _componenteCache = componentes?.data?.find((c: any)=> c?.id === _c?.componente_id);
                return _componenteCache?.categoria_componentes_id === 1;
              });*/
              const modulos = projeto?.componentes?.filter((_comp: any)=>{
                const _componenteCache = componentes?.data?.find((c: any)=> c?.id === _comp?.componente_id);
                return _componenteCache?.categoria_componentes_id === 1;
              })
              /*
              if (modulo){
                const _modelo = componentes?.data?.find((componente: any) => componente.id === modulo?.componente_id) || null;
                modelo = _modelo?.modelo || null;
              }*/
              if (modulos?.length){
                modulos?.forEach((x: any)=>{
                  const _modelo = componentes?.data?.find((componente: any) => componente.id === x.componente_id) || null;
                  if(!modelo){
                    modelo += `${_modelo?.modelo}`
                  }else{
                    modelo += `/${_modelo?.modelo}`;
                  }
                })
              }
            }/*
            if (projeto?.solucao_sugerida && projeto?.tipo === "dimensionado"){
              const modulo = projeto?.solucao_sugerida?.componentes?.find((_c: any) => {
                const _componenteCache = componentes?.data?.find((c: any) => c?.id === _c?.id);
                return _componenteCache?.categoria_componentes_id === 1;
              });
  
              if (modulo) {
                  const _modelo = componentes?.data?.find((componente: any) => componente.id === modulo?.id) || null;
                  modelo = _modelo?.modelo || null; 
              }
            }else if(projeto?.tipo === "personalizado" || projeto?.tipo === "kit"){
              const modulo = projeto?.componentes?.find((_c: any)=>{
                const _componenteCache = componentes?.data?.find((c: any)=> c?.id === _c?.componente_id);
                return _componenteCache?.categoria_componentes_id === 1;
              });

              if (modulo){
                const _modelo = componentes?.data?.find((componente: any) => componente.id === modulo?.componente_id) || null;
                modelo = _modelo?.modelo || null;
              }
            }*/
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{modelo || "-"}</TableCell>)
          })}
        </>)
      case "disposicao":
        return(<>
          {projetos.map((_historico: any)=>{
            let fileiraProjetos: ReactNode | null = null;
            let tableFileiras: ReactNode | null = null;
            const {projeto} = _historico;
            if (projeto?.solucao_sugerida && projeto?.tipo === "dimensionado") {
              fileiraProjetos = projeto?.solucao_sugerida?.fileiras?.map((fileira: any) => {
                const _estrutura = estruturas?.data?.find((estrutura: any) => estrutura?.id === fileira?.estrutura_id)?.nome || 'Sem estrutura';
                return <>
                    <TableRow>
                        <TableCell>{_estrutura}</TableCell>
                        <TableCell>{fileira?.fileiras_iguais}</TableCell>
                        <TableCell>{fileira?.modulos_sequencia}</TableCell>
                        <TableCell>{fileira?.posicao === "retrato" ? "Retrato" : "Paisagem"}</TableCell>
                    </TableRow>
                </>
            }); 
            }
            if (fileiraProjetos) {
              tableFileiras = 
              <TableContainer>
                  <Table size="small">
                      <TableHead>
                          <TableRow>
                              <TableCell style={{fontWeight: 'bold'}} width="auto">Estrutura</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} width="auto" /*align="right"*/>Fileiras iguais</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} width="auto">Modulos sequência</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} width="auto">Posição</TableCell>
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {fileiraProjetos}
                      </TableBody>
                  </Table>
              </TableContainer>
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{tableFileiras || "-"}</TableCell>)
          })}
        </>)
      case "tipo_proposta":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const projetoV1 = projeto?.dados_migracao?.projetoV1;
            let tipo = projeto?.valor_servico ? "Gerador com Serviço de Instalação" : "Apenas Gerador sem Instalação";
            if (!projeto?.migrado && projeto?.origem === ORIGEM_HISTORICO_V1 && projetoV1?.TipoPagamento) {
              tipo = getTipoPagamento(projetoV1?.TipoPagamento);
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{tipo || "-"}</TableCell>)
          })}
        </>)
      case "percentual_comissao_distribuidor":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString(projeto?.percentual_comissao_distribuidor || 0, TypeRecords.DECIMAL2) + "%" || "-"}</TableCell>)
          })}
        </>)
      case "valor_produtos":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((projeto?.valor || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_produtos_desconto":
        return(<>
          {projetos.map((_historico: any, _index: number)=>{
            const {projeto} = _historico;
            const pedido = props?.pedido;
            const ultimoHistorico: boolean = _index === projetos?.length -1;
            const descontoPedido: boolean = ultimoHistorico 
              && (((projeto?.valor_desconto || 0) + (pedido?.valor_desconto || 0)) < (projeto?.valor || 0));
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((handleValorDesconto(projeto, pedido, descontoPedido) || (projeto?.valor_projeto || 0) || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_margem":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((projeto?.valor_margem_produtos || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_instalacao":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((projeto?.valor_servico || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_servico":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString(((projeto?.valor_servico || 0) + (projeto?.valor_margem_produtos || 0)), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_servico_pis_cofins":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((projeto?.valor_servico_pis_cofins || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "valor_total":
        return(<>
          {projetos.map((_historico: any, _index: number)=>{
            const {projeto} = _historico;
            const pedido = props?.pedido;
            const ultimoHistorico: boolean = _index === projetos?.length -1;
            const descontoPedido: boolean = ultimoHistorico 
              && (((projeto?.valor_desconto || 0) + (pedido?.valor_desconto || 0)) < (projeto?.valor || 0));
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{Formater.toString((handleValorTotal(projeto, pedido, descontoPedido) || (projeto?.valor_projeto || 0) || 0), TypeRecords.CURRENCY) || "-"}</TableCell>)
          })}
        </>)
      case "termos_aceite":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const projetoV1 = projeto?.dados_migracao?.projetoV1;
            
            let termos: ReactNode | null = null;
            if (projeto?.origem === ORIGEM_HISTORICO_V1 && projetoV1?.TextoConfirmarVisitaTecnica) {
              termos = (
                <div className="X12" style={{marginBottom: "10px"}}>
                  <Alert severity="success">
                    <div dangerouslySetInnerHTML={{__html: projetoV1?.TextoConfirmarVisitaTecnica || ""}}/>
                  </Alert>
                </div>)
            } else if (projeto?.termos_aceite) {
              termos = renderTermosVisitaTecnica(projeto)
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{termos || "-"}</TableCell>)
          })}
        </>)
      case "endereco":
        return (
          <>
            {projetos.map((_historico: any)=>{
              const {projeto} = _historico;
              let endereco = "";
              const semEndereco = (!projeto?.endereco || !projeto?.endereco?.logradouro);
              if (projeto?.origem === 'V1' && semEndereco && projeto?.endereco_proposta) {
                endereco = projeto.endereco_proposta;
              } else if(projeto?.endereco) {
                if(projeto?.endereco?.cidade_id){
                  const cidade = cidades?.data?.find((cidade: any) => cidade.id === projeto?.endereco?.cidade_id);
                  if (projeto?.endereco?.logradouro){
                    endereco += projeto?.endereco?.logradouro;
                  }
                  if (endereco){
                      if(projeto?.endereco?.numero){
                        endereco += `, ${projeto?.endereco?.numero}`;
                      }else endereco += ', SN';
                  }

                  if (projeto?.endereco?.bairro && endereco){
                    endereco += ` - ${projeto?.endereco?.bairro}`;
                  }

                  if (cidade && projeto?.endereco?.estado_id && endereco){
                    endereco += ` - ${cidade?.nome}/${projeto?.endereco?.estado_id}`;
                  }
                  if (projeto?.endereco?.cep && endereco){
                    endereco += ` - ${projeto?.endereco?.cep?.replace(/^(\d{5})(\d)/, "$1-$2")}`
                  }
                  if(!endereco && cidade){
                    endereco += `${cidade?.nome}/${projeto?.endereco?.estado_id}`;
                  }
                }
              }
              return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{endereco ||"-"}</TableCell>)
            })}
          </>
        );
      case "consumo":
        return(<>
          {projetos.map((_historico: any)=>{
            let consumoList: ReactNode | null = null;
            const {projeto} = _historico;
            if (projeto?.solucao_sugerida?.consumo?.length){
              const consumo = arraySort(projeto?.solucao_sugerida?.consumo, 'dia', 'date');
              consumoList = <ul>
                {consumo?.map((cons: any) =>{
                  const _dia = moment(cons?.dia).format("MM/YYYY")
                  return <li key={cons.dia}>
                    <strong>{_dia}</strong> - {cons?.consumo} kWh
                  </li>
                })}
                
              </ul>
            }
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{consumoList ||"-"}</TableCell>)
          })}
        </>)
      case "estabelecimento_id":
        return(<>
          {projetos?.map((_historico: any) => {
            const pedido = props?.pedido;
            let estabelecimentoId = _historico?.projeto?.[campo];
            if (!estabelecimentoId && pedido?.estabelecimento_id) {
              estabelecimentoId = pedido?.estabelecimento_id;
            }
            return(
              <TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{estabelecimentoId || "-"}</TableCell>
            )
          })}</>)
      case "justificativa_valor_servico":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{projeto?.justificativa_valor_servico || "-"}</TableCell>)
          })}
        </>)
      case "valor_watt_servico":
        return(<>
          {projetos.map((_historico: any)=>{
            const {projeto} = _historico;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{projeto?.valor_watt_servico ? Formater.toString((projeto?.valor_watt_servico || 0), TypeRecords.CURRENCY) : "-"}</TableCell>)
          })}
        </>)

      case "equipamentos":
        return(<>
          {projetos.map((_historico: any)=>{
            let bodyEquipamentos: ReactNode | null = null;
            let tableEquipamentos: ReactNode | null = null;
            const { projeto } = _historico;
            if (projeto?.equipamentos){
              const _equipamentos = projeto?.equipamentos?.filter((i:any) =>i.quantidade > 0/* && i.deleted_at === null*/);
              
              bodyEquipamentos = _equipamentos?.map((equip: any)=>{
                let removed: any[] = [];
                let added: any[] = [];
                let edited: any[] = [];
                
                let color = '';
                const firstProject = projetos[0]?.projeto;
                const findFirst = firstProject?.equipamentos?.filter((i:any) =>i.quantidade > 0)?.find((i:any) => i?.id === equip?.id);
                const current = projetos?.find((i:any) => Number(i?.id) === Number(_historico?.id));
                const findCurrent = current?.projeto?.equipamentos?.find((ii:any) => ii?.id === equip?.id);
                const wasRemoved = allRemoved?.find((id:any) => id === equip?.id);
                
                if (projetos?.length > 1) {
                  if (!findFirst || (wasRemoved && findCurrent)) { 
                    color = green[200];
                    added.push(equip?.id);
                  } else if (
                    findFirst?.quantidade !== equip?.quantidade 
                    || findFirst?.nome !== equip?.nome
                    || findFirst?.potencia !== equip?.potencia
                    || findFirst?.horas_uso !== equip?.horas_uso
                    || findFirst?.consumo_diaria !== equip?.consumo_diaria
                  ) {
                    color = orange[200];
                    edited.push(equip?.id);
                  }
                  
                  const filter = projetos?.filter((i:any) => Number(i?.id) !== Number(_historico?.id));
                  filter?.forEach((i:any) => {
                    const found = i?.projeto?.equipamentos?.find((ii:any) => ii?.id === equip?.id);
                    if (!found && !added?.includes(equip?.id) && !edited?.includes(equip?.id)) {
                      removed.push(equip?.id);
                      if (!allRemoved?.includes(equip?.id)) allRemoved.push(equip?.id);
                      return;
                    }
                  })

                  if (removed?.includes(equip?.id)) color = red[200];
                }
                
                return(<TableRow key={equip?.id} style={{ backgroundColor: color }}>
                  <TableCell align="left">{equip?.nome}</TableCell>
                  <TableCell align="left">{equip?.potencia}</TableCell>
                  <TableCell align="left">{equip?.quantidade}</TableCell>
                  <TableCell align="left">{equip?.horas_uso}</TableCell>
                  <TableCell align="left">{equip?.consumo_diaria}</TableCell>
                </TableRow>)
              })
            }
            if(bodyEquipamentos){
              tableEquipamentos = 
              <TableContainer>
                  <Table size="small">
                      <TableHead>
                          <TableRow>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Descrição</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Potência (w)</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Quantidade</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Horas de uso por dia (horas)</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Consumo diário (Wh/dia)</TableCell> 
                          </TableRow>
                      </TableHead>
                      <TableBody>
                        {bodyEquipamentos}
                      </TableBody>
                  </Table>
              </TableContainer>
            }
            return(<TableCell className={classes.historico_selecionado}>{tableEquipamentos || "-"}</TableCell>)
          })}
        </>)
      case "total_consumo_diario_w":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const totalConsumDiarioW = projeto?.solucao_sugerida?.total_consumo_diario_w ? Formater.toString(projeto?.solucao_sugerida?.total_consumo_diario_w, TypeRecords.DECIMAL2) : null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{totalConsumDiarioW || "-"}</TableCell>)
          })}
        </>)      
      case "fator_demanda":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const fatorDemanda = Formater.toString(projeto?.solucao_sugerida?.fator_demanda || 0, TypeRecords.DECIMAL2) + "%"; 
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{fatorDemanda}</TableCell>)
          })}
        </>)
      case "tipo_bateria":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const tipoBateria = projeto?.solucao_sugerida?.tipo_bateria || null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{parseTipoBateria(tipoBateria) || "-"}</TableCell>)
          })}
        </>)
      case "autonomia_banco_baterias":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const autonomiaBancoBaterias = projeto?.solucao_sugerida?.autonomia_banco_baterias || null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{autonomiaBancoBaterias || "-"}</TableCell>)
          })}
        </>)          
      case "tensao_saida":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const tensaoSaida = projeto?.solucao_sugerida?.tensao_saida || "";
            if (dimensionadoHibrido) {
              return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{parseTensaoOnGrid(tensaoSaida) || '-'}</TableCell>)
            } else {
              return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{parseTensaoOffgrid(tensaoSaida) || '-'}</TableCell>)
            }
          })}
        </>)    
      case "sugestao_instalacao":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const  sugestaoInstalacao = projeto?.sugestao_instalacao?.texto ? (
              <div className="X12" style={{marginBottom: "10px"}}>
                <Alert severity="success" className='box-sugestao-instalacao'>
                  <div dangerouslySetInnerHTML={{__html: projeto?.sugestao_instalacao?.texto || ""}}/>
                </Alert>
              </div>) : ""

            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{sugestaoInstalacao || "-"}</TableCell>)
          })}
        </>)

      case "sugestao_instalacao_alerta":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const  sugestaoInstalacaoAlerta = projeto?.sugestao_instalacao?.alerta ? (
              <div className="X12" style={{marginBottom: "10px"}}>
                <Alert severity="warning">
                  <div dangerouslySetInnerHTML={{__html: projeto?.sugestao_instalacao?.alerta || ""}}/>
                </Alert>
                {
                  projeto?.sugestao_instalacao?.confirmado_em 
                    ? (<p><strong>“Estou ciente”</strong> confirmado dia {moment(projeto?.sugestao_instalacao?.confirmado_em).format("DD/MM/YYYY")} pelo usuário {projeto?.sugestao_instalacao?.user_name}</p>) 
                    : (<></>)
                }
              </div>) : ""

            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{sugestaoInstalacaoAlerta || "-"}</TableCell>)
          })}
        </>)

      case "cargas_prioritarias":
        return(<>
          {projetos.map((_historico: any)=>{
            let bodyCargasPrioritarias: ReactNode | null = null;
            let tableCargasPrioritarias: ReactNode | null = null;
            const { projeto } = _historico;
            if (projeto?.cargas_prioritarias?.length){
              const _cargasPrioritarias = projeto?.cargas_prioritarias?.filter((i:any) =>i.quantidade > 0/* && i.deleted_at === null*/);
              
              bodyCargasPrioritarias = _cargasPrioritarias?.map((item: any)=>{
                let removed: any[] = [];
                let added: any[] = [];
                let edited: any[] = [];
                
                let color = '';
                const firstProject = projetos[0]?.projeto;
                const findFirst = firstProject?.cargas_prioritarias?.filter((i:any) =>i.quantidade > 0)?.find((i:any) => i?.id === item?.id);
                const current = projetos?.find((i:any) => Number(i?.id) === Number(_historico?.id));
                const findCurrent = current?.projeto?.cargas_prioritarias?.find((ii:any) => ii?.id === item?.id);
                const wasRemoved = allRemoved?.find((id:any) => id === item?.id);
                
                if (projetos?.length > 1) {
                  if (!findFirst || (wasRemoved && findCurrent)) { 
                    color = green[200];
                    added.push(item?.id);
                  } else if (
                    findFirst?.quantidade !== item?.quantidade 
                    || findFirst?.nome !== item?.nome
                    || findFirst?.potencia !== item?.potencia
                    || findFirst?.horas_uso !== item?.horas_uso
                    || findFirst?.consumo_diaria !== item?.consumo_diaria
                    || findFirst?.relacao_partida !== item?.relacao_partida
                  ) {
                    color = orange[200];
                    edited.push(item?.id);
                  }
                  
                  const filter = projetos?.filter((i:any) => Number(i?.id) !== Number(_historico?.id));
                  filter?.forEach((i:any) => {
                    const found = i?.projeto?.cargas_prioritarias?.find((ii:any) => ii?.id === item?.id);
                    if (!found && !added?.includes(item?.id) && !edited?.includes(item?.id)) {
                      removed.push(item?.id);
                      if (!allRemoved?.includes(item?.id)) allRemoved.push(item?.id);
                      return;
                    }
                  })

                  if (removed?.includes(item?.id)) color = red[200];
                }
                //Ajuste para atender tanto historicos antigos quanto novos
                const consumoDiario = item?.consumo_diaria ? item?.consumo_diaria : (item?.potencia || 0) * (item?.quantidade || 1) * item?.horas_uso;
                return(<TableRow key={item?.id} style={{ backgroundColor: color }}>
                  <TableCell align="left">{item?.nome}</TableCell>
                  <TableCell align="left">{item?.potencia}</TableCell>
                  <TableCell align="left">{item?.quantidade}</TableCell>
                  <TableCell align="left">{item?.horas_uso}</TableCell>
                  <TableCell align="left">{item?.fator_potencia}</TableCell>
                  <TableCell align="left">{item?.relacao_partida}</TableCell>
                  <TableCell align="left">{consumoDiario}</TableCell>
                </TableRow>)
              })
            }
            if(bodyCargasPrioritarias){
              tableCargasPrioritarias = 
              <TableContainer>
                  <Table size="small">
                      <TableHead>
                          <TableRow>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Descrição (Nome do equipamento)</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Potência (w)</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Quantidade</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Horas de uso (h)</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Fator de potência</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Relação de partida</TableCell>
                              <TableCell style={{fontWeight: 'bold'}} align="left">Consumo (wh)</TableCell> 
                          </TableRow>
                      </TableHead>
                      <TableBody>
                        {bodyCargasPrioritarias}
                      </TableBody>
                  </Table>
              </TableContainer>
            }
            return(<TableCell className={classes.historico_selecionado}>{tableCargasPrioritarias || "-"}</TableCell>)
          })}
        </>)
      case "total_carga_equipamentos_kw":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const totalCargaEquipamentos = projeto?.solucao_sugerida?.total_carga_equipamentos_kw ? Formater.toString(projeto?.solucao_sugerida?.total_carga_equipamentos_kw) : null;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{totalCargaEquipamentos ? Number(totalCargaEquipamentos)?.toFixed(2) : '-'}</TableCell>)
          })}
        </>)      
      case "taxa_descarga":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const taxaDescarga = projeto?.solucao_sugerida?.taxa_descarga; 
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{taxaDescarga ? `${Number(taxaDescarga)?.toFixed(1)}C` : '-'}</TableCell>)
          })}
        </>)
      case "tipo_bateria_hibrida":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const tipoBateria = projeto?.solucao_sugerida?.tipo_bateria || null;
            const bateria = componentes?.data?.find((i: any) => i?.id === tipoBateria)?.nome || tipoBateria;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{bateria || "-"}</TableCell>)
          })}
        </>)
      case "tipo_solucao":
        return(<>
          {projetos.map((_historico: any)=>{
            const { projeto } = _historico;
            const tipoSolucao = projeto?.solucao_sugerida?.tipo_solucao || null;
            const solucao = TIPOS_SOLUCAO_OPTIONS.find(i => i.value === tipoSolucao)?.text;
            return(<TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{solucao || "-"}</TableCell>)
          })}
        </>)

      default:
        return(<>
        {projetos.map((_historico: any)=>{
          return(
            <TableCell className={changed ? classes.alterado : classes.historico_selecionado}>{_historico?.projeto?.[campo] || "-"}</TableCell>
          )
        })}</>)
    }})
  useEffect(()=>{
    if(categoriasComponentes?.data?.length){
      setCategoriaComponentesState(categoriasComponentes?.data)
    }
  },[categoriasComponentes])
  return (<>
    <TableContainer className='table-historico' sx={{ overflowX: "auto" }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    {props.dados.map(_historico=>{
                      return(
                        <TableCell 
                          key={_historico.id}
                          style={{fontWeight: "bold"}}
                        >
                          Projeto em: {maskDate(_historico?.data_alteracao || "", true)}
                        </TableCell>
                      )
                    })}                    
                </TableRow>
            </TableHead>
            <TableBody>
                {!avulso && (<>
                  <TableRow>
                      <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Solução Sugerida</TableCell>
                  </TableRow>
                  <TableRow className={`row ${props?.changeState?.solucao_sugerida?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Solução Sugerida</TableCell>
                      {compareRow("solucao_sugerida", props.dados, props?.changeState?.solucao_sugerida?.hasChanged)}
                  </TableRow>
                </>)}
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Componentes</TableCell>
                </TableRow>
                <TableRow className={`row ${props?.changeState?.componentes?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Componentes</TableCell>
                    {compareRow("componentes", props.dados, props?.changeState?.componentes?.hasChanged)}
                </TableRow>
                {dimensionadoOffgrid && 
                  <>
                    <TableRow>
                        <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Equipamentos</TableCell>
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.equipamentos?.hasChanged ? classes.alterado : ""}`}>
                        <TableCell className={classes.titulo}>Equipamentos</TableCell>
                        {compareRow("equipamentos", props.dados, props?.changeState?.equipamentos?.hasChanged)}
                    </TableRow>
                  </>
                }
                {dimensionadoHibrido && 
                  <>
                    <TableRow>
                        <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Equipamentos</TableCell>
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.cargas_prioritarias?.hasChanged ? classes.alterado : ""}`}>
                        <TableCell className={classes.titulo}>Equipamentos</TableCell>
                        {compareRow("cargas_prioritarias", props.dados, props?.changeState?.cargas_prioritarias?.hasChanged)}
                    </TableRow>
                  </>
                }
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Projeto</TableCell>
                </TableRow>
                <TableRow className={`row ${props?.changeState?.codigo_venda?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Código Projeto</TableCell>
                    {compareRow("codigo_venda", props.dados, props?.changeState?.codigo_venda?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.titulo?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Título</TableCell>
                    {compareRow("titulo", props.dados, props?.changeState?.titulo?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.distribuidor?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Distribuidor</TableCell>
                    {compareRow("distribuidor", props.dados, props?.changeState?.distribuidor?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.revenda?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Revenda</TableCell>
                    {compareRow("revenda", props.dados, props?.changeState?.revenda?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.status_projeto?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Etapa de Venda</TableCell>
                    {compareRow("status_projeto", props.dados, props?.changeState?.status_projeto?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.status_projeto_pagamento?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Status de Pagamento</TableCell>
                    {compareRow("status_projeto_pagamento", props.dados, props?.changeState?.status_projeto_pagamento?.hasChanged)}
                </TableRow>
                {!avulso && (<TableRow className={`row ${props?.changeState?.expirado?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Expirado</TableCell>
                    {compareRow("expirado", props.dados, props?.changeState?.expirado?.hasChanged)}
                </TableRow>)}
                {exibeCamposTermosProjeto && (
                  <>
                    <TableRow className={`row ${props?.changeState?.aceitou_termos?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Aceitou os termos</TableCell>
                      {compareRow("aceitou_termos", props.dados, props?.changeState?.aceitou_termos?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.termos_projeto?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Termos do projeto</TableCell>
                      {compareRow("termos_projeto", props.dados, props?.changeState?.termos_projeto?.hasChanged)}
                    </TableRow>
                  </>
                )}
                <TableRow className={`row ${props?.changeState?.previsao_faturamento?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Previsão de faturamento</TableCell>
                    {compareRow("previsao_faturamento", props.dados, props?.changeState?.previsao_faturamento?.hasChanged)}
                </TableRow>
                {!avulso && (<TableRow className={`row ${props?.changeState?.tipo_faturamento?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Tipo de Faturamento</TableCell>
                    {compareRow("tipo_faturamento", props.dados, props?.changeState?.tipo_faturamento?.hasChanged)}
                </TableRow>)}
                <TableRow className={`row ${props?.changeState?.tipo?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Tipo de Projeto</TableCell>
                    {compareRow("tipo", props.dados, props?.changeState?.tipo?.hasChanged)}
                </TableRow>
                {/*INICIO SECAO DE DADOS DE CLIENTES*/}
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Cliente</TableCell>
                </TableRow>
                <TableRow className={`row ${props?.changeState?.cliente_proposta?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Nome</TableCell>
                    {compareRow("cliente_proposta", props.dados, props?.changeState?.cliente_proposta?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.cpf_cnpj_proposta?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>CPF/CNPJ</TableCell>
                    {compareRow("cpf_cnpj_proposta", props.dados, props?.changeState?.cpf_cnpj_proposta?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.email_proposta?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>E-mail</TableCell>
                    {compareRow("email_proposta", props.dados, props?.changeState?.email_proposta?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.endereco?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Endereço</TableCell>
                    {compareRow("endereco", props.dados, props?.changeState?.endereco?.hasChanged)}
                </TableRow>
                {/*FIM SECAO DE DADOS DE CLIENTES*/}
                {/*INICIO SECAO DE DADOS DA CONFIGURACAO*/}
                {!avulso && <>
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Configuração</TableCell>
                </TableRow>
                { dimensionadoOffgrid && (<>
                    <TableRow className={`row ${props?.changeState?.total_consumo_diario_w?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Consumo total diário (Wh/dia)</TableCell>
                      {compareRow("total_consumo_diario_w", props.dados, props?.changeState?.total_consumo_diario_w?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tipo_bateria?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tipo de Bateria</TableCell>
                      {compareRow("tipo_bateria", props.dados, props?.changeState?.tipo_bateria?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.autonomia_banco_baterias?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Autonomia para o banco de baterias (horas)</TableCell>
                      {compareRow("autonomia_banco_baterias", props.dados, props?.changeState?.autonomia_banco_baterias?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tensao_saida?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tensão de saída (V)</TableCell>
                      {compareRow("tensao_saida", props.dados, props?.changeState?.tensao_saida?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.potencia?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência do gerador</TableCell>
                      {compareRow("potencia", props.dados, props?.changeState?.potencia?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.nome_modulo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Nome do módulo</TableCell>
                      {compareRow("nome_modulo", props.dados, props?.changeState?.nome_modulo?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.disposicao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Disposição dos módulos</TableCell>
                      {compareRow("disposicao", props.dados, props?.changeState?.disposicao?.hasChanged)}
                    </TableRow>
                  </>
                  )}
                  { dimensionadoHibrido && (<>
                    <TableRow className={`row ${props?.changeState?.total_carga_equipamentos_kw?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Carga dos Equipamentos (kW/dia)</TableCell>
                      {compareRow("total_carga_equipamentos_kw", props.dados, props?.changeState?.total_carga_equipamentos_kw?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tipo_bateria?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tipo de Bateria</TableCell>
                      {compareRow("tipo_bateria_hibrida", props.dados, props?.changeState?.tipo_bateria?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.taxa_descarga?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Taxa de descarga</TableCell>
                      {compareRow("taxa_descarga", props.dados, props?.changeState?.taxa_descarga?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tensao_saida?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tensão de saída (V)</TableCell>
                      {compareRow("tensao_saida", props.dados, props?.changeState?.tensao_saida?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.potencia?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência do gerador (Potência desejada)</TableCell>
                      {compareRow("potencia", props.dados, props?.changeState?.potencia?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.nome_modulo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Nome do módulo</TableCell>
                      {compareRow("nome_modulo", props.dados, props?.changeState?.nome_modulo?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.disposicao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Disposição dos módulos</TableCell>
                      {compareRow("disposicao", props.dados, props?.changeState?.disposicao?.hasChanged)}
                    </TableRow>
                  </>
                  )}
                  {personalizadoOffgrid && (<>
                    <TableRow className={`row ${props?.changeState?.potencia?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência do gerador</TableCell>
                      {compareRow("potencia", props.dados, props?.changeState?.potencia?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.nome_modulo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Nome do módulo</TableCell>
                      {compareRow("nome_modulo", props.dados, props?.changeState?.nome_modulo?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tipo_solucao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tipo de Solução</TableCell>
                      {compareRow("tipo_solucao", props.dados, props?.changeState?.tipo_solucao?.hasChanged)}
                    </TableRow>
                  </>)}
                  {personalizadoHibrido && (<>
                    <TableRow className={`row ${props?.changeState?.tipo_inversor?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tipo Inversor</TableCell>
                      {compareRow("tipo_inversor", props.dados, props?.changeState?.tipo_inversor?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.potencia?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência do gerador</TableCell>
                      {compareRow("potencia", props.dados, props?.changeState?.potencia?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.nome_modulo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Nome do módulo</TableCell>
                      {compareRow("nome_modulo", props.dados, props?.changeState?.nome_modulo?.hasChanged)}
                    </TableRow>
                  </>)}
                  {(!dimensionadoOffgrid && !dimensionadoHibrido && !personalizadoOffgrid && !personalizadoHibrido) && (<>
                    <TableRow className={`row ${props?.changeState?.tensao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tensão</TableCell>
                      {compareRow("tensao", props.dados, props?.changeState?.tensao?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.inversor_tipo_conexao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Fase</TableCell>
                      {compareRow("inversor_tipo_conexao", props.dados, props?.changeState?.inversor_tipo_conexao?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.tipo_inversor?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Tipo Inversor</TableCell>
                      {compareRow("tipo_inversor", props.dados, props?.changeState?.tipo_inversor?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.fator_perdas?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Fator de perdas (%)</TableCell>
                      {compareRow("fator_perdas", props.dados, props?.changeState?.fator_perdas?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.concessionaria?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Concessionária</TableCell>
                      {compareRow("concessionaria", props.dados, props?.changeState?.concessionaria?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.simultaneidade?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Simultaneidade</TableCell>
                      {compareRow("simultaneidade", props.dados, props?.changeState?.simultaneidade?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.potencia_desejada?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência desejada</TableCell>
                      {compareRow("potencia_desejada", props.dados, props?.changeState?.potencia_desejada?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.potencia?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Potência do gerador</TableCell>
                      {compareRow("potencia", props.dados, props?.changeState?.potencia?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.consumo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Consumo</TableCell>
                      {compareRow("consumo", props.dados, props?.changeState?.consumo?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.preco_kwh?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Preço do kWh</TableCell>
                      {compareRow("preco_kwh", props.dados, props?.changeState?.preco_kwh?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.nome_modulo?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Nome do módulo</TableCell>
                      {compareRow("nome_modulo", props.dados, props?.changeState?.nome_modulo?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.disposicao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Disposição dos módulos</TableCell>
                      {compareRow("disposicao", props.dados, props?.changeState?.disposicao?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.sugestao_instalacao?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Sugestão Instalação</TableCell>
                      {compareRow("sugestao_instalacao", props.dados, props?.changeState?.sugestao_instalacao?.hasChanged)}
                    </TableRow>
                    <TableRow className={`row ${props?.changeState?.sugestao_instalacao_alerta?.hasChanged ? classes.alterado : ""}`}>
                      <TableCell className={classes.titulo}>Mensagem de Alerta</TableCell>
                      {compareRow("sugestao_instalacao_alerta", props.dados, props?.changeState?.sugestao_instalacao_alerta?.hasChanged)}
                    </TableRow>
                  </>)
                }</>}

                {/*FIM SECAO DE DADOS DA CONFIGURACAO*/}
                {/*INICIO SECAO DE DADOS FINANCEIROS*/}
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Financeiro</TableCell>
                </TableRow>
                {!avulso && (<TableRow className={`row ${props?.changeState?.tipo_proposta?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Tipo Proposta</TableCell>
                    {compareRow("tipo_proposta", props.dados, props?.changeState?.tipo_proposta?.hasChanged)}
                </TableRow>)}
                <TableRow className={`row ${props?.changeState?.percentual_comissao_distribuidor?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Percentual do Distribuidor</TableCell>
                    {compareRow("percentual_comissao_distribuidor", props.dados, props?.changeState?.percentual_comissao_distribuidor?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.valor_produtos?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Produtos</TableCell>
                    {compareRow("valor_produtos", props.dados, props?.changeState?.valor_produtos?.hasChanged)}
                </TableRow>
                <TableRow className={`row ${props?.changeState?.valor_produtos_desconto?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Produtos com Desconto</TableCell>
                    {compareRow("valor_produtos_desconto", props.dados, props?.changeState?.valor_produtos_desconto?.hasChanged)}
                </TableRow>
                {!avulso && (<TableRow className={`row ${props?.changeState?.valor_instalacao?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Instalação</TableCell>
                    {compareRow("valor_instalacao", props.dados, props?.changeState?.valor_instalacao?.hasChanged)}
                </TableRow>)}
                {!avulso && (<TableRow className={`row ${props?.changeState?.valor_margem?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Margem</TableCell>
                    {compareRow("valor_margem", props.dados, props?.changeState?.valor_margem?.hasChanged)}
                </TableRow>)}
                {!avulso && (<TableRow className={`row ${props?.changeState?.valor_servico?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Serviço</TableCell>
                    {compareRow("valor_servico", props.dados, props?.changeState?.valor_servico?.hasChanged)}
                </TableRow>)}
                {!avulso && (<TableRow className={`row ${props?.changeState?.valor_servico_pis_cofins?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Serviço com PIS/COFINS</TableCell>
                    {compareRow("valor_servico_pis_cofins", props.dados, props?.changeState?.valor_servico_pis_cofins?.hasChanged)}
                </TableRow>)}
                {!avulso && (<TableRow className={`row ${props?.changeState?.valor_watt_servico?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Watt do Serviço</TableCell>
                    {compareRow("valor_watt_servico", props.dados, props?.changeState?.valor_watt_servico?.hasChanged)}
                </TableRow>)}
                {!avulso && (<TableRow className={`row ${props?.changeState?.justificativa_valor_servico?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Justificativa</TableCell>
                    {compareRow("justificativa_valor_servico", props.dados, props?.changeState?.justificativa_valor_servico?.hasChanged)}
                </TableRow>)}
                <TableRow className={`row ${props?.changeState?.valor_total?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Valor Total</TableCell>
                    {compareRow("valor_total", props.dados, props?.changeState?.valor_total?.hasChanged)}
                </TableRow>
                {/*FIM SECAO DE DADOS FINANCEIROS*/}
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Logística</TableCell>
                </TableRow>
                <TableRow className={`row ${props?.changeState?.estabelecimento_id?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Estabelecimento</TableCell>
                    {compareRow("estabelecimento_id", props.dados, props?.changeState?.estabelecimento_id?.hasChanged)}
                </TableRow>
                <TableRow>
                    <TableCell className={classes.header} colSpan={props?.dados?.length+1 || 1}>Termos de Aceite</TableCell>
                </TableRow>
                <TableRow className={`row ${props?.changeState?.termos_aceite?.hasChanged ? classes.alterado : ""}`}>
                    <TableCell className={classes.titulo}>Termos de Aceite</TableCell>
                    {compareRow("termos_aceite", props.dados, props?.changeState?.termos_aceite?.hasChanged)}
                </TableRow>

            </TableBody>            
        </Table>
    </TableContainer>
  </>);
};

export default TabelaComparacaoProjetos;
